import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ActionButton } from '@/components/ActionButton';
import Docket from './Docket';
import printerImage from './handheld-printer.png';
import { getPrinterBaseUrl } from './printerUrls';

// TODO: Add DECLINE_RESPONSES...

const StandalonePrinterEmulatorPage = () => {
  const { imei } = Route.useParams();
  const printerId = imei;
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchData>> | null>(null);
  const [isError, setIsError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchPrinterData = async () => {
      if (!printerId) return;
      setIsFetching(true);
      setIsError(false);
      try {
        const result = await fetchData(printerId);
        setData(result);
      } catch (error) {
        setIsError(true);
      }
      setIsFetching(false);
    };
    fetchPrinterData();
  }, [printerId]);

  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);

  useEffect(() => {
    setIsAwaitingResponse(!!data?.orderId);
  }, [data?.orderId]);

  const handleAcceptOrder = async (
    printerId: string,
    orderId: string,
    printerResponse: AcceptResponse,
  ) => {
    setIsAwaitingResponse(false);
    const response = await acceptOrder(printerId, orderId, printerResponse);
    if (response !== 'OK') {
      setIsAwaitingResponse(true);
    }
  };

  return (
    <div>
      <div>
        <div>IMEI: {imei}</div>
        {isError && (
          <div className="bg-red-300 p-2">
            There was a network error.
            <br />
            You may need to adjust your browser settings (for this site), to allow mixed content
            (HTTP).
            <br />
            chrome://settings/content/insecureContent?search=Insecure+content
          </div>
        )}
        <div className="p-4">
          <div className="relative mx-auto" style={{ width: '224px' }}>
            <img src={printerImage} alt="Printer" />
            <div
              style={{
                textAlign: 'center',
                color: 'black',
                backgroundColor: '#577f42',
                fontWeight: 'bold',
                fontSize: '12px',
                width: '160px',
                height: '105px',
                lineHeight: '100px',
                position: 'absolute',
                top: '242px',
                left: '31px',
                borderRadius: '4px',
              }}
            >
              {isFetching
                ? 'Fetching...'
                : isAwaitingResponse
                  ? 'Choose a response'
                  : `fetch in ${FETCH_INTERVAL_IN_SEC} seconds`}
            </div>

            {isAwaitingResponse && data?.orderId && (
              <div className="mt-6 grid grid-cols-2 gap-2">
                {ACCEPT_RESPONSES.map((response) => (
                  <ActionButton
                    key={response}
                    onClick={() => handleAcceptOrder(printerId, data.orderId, response)}
                    className={'bg-green-700 hover:bg-green-800 text-white'}
                  >
                    {response.split('_').join(' ')}
                  </ActionButton>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={'pt-[72px]'}>
        {data?.docketText && isAwaitingResponse && <Docket docketText={data?.docketText} />}
      </div>
    </div>
  );
};

export const Route = createFileRoute('/a/standalone-printers/$imei/emulator')({
  component: StandalonePrinterEmulatorPage,
});

const FETCH_INTERVAL_IN_SEC = 5;

// 'OK' status is used for non-realtime orders (eg catering, bulk meal prep orders)
const ACCEPT_RESPONSES = ['10_Minutes', '20_Minutes', '30_Minutes', '60_Minutes', 'OK'] as const;
type AcceptResponse = (typeof ACCEPT_RESPONSES)[number];

const fetchData = async (printerId: string) => {
  const printerBaseUrl = getPrinterBaseUrl();
  const password = printerId.slice(-4);
  const response = await fetch(
    `${printerBaseUrl}/fetch?u=${printerId}&p=${password}&a=00&cur=2020150908`,
    {
      method: 'GET',
      mode: 'cors',
      headers: {
        Accept: 'text/html,application/xhtml+xml',
      },
    },
  );
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const htmlText = await response.text();

  if (!htmlText) return null;

  return {
    orderId: getOrderId(htmlText),
    docketText: formatReceiptText(htmlText),
  };
};

function formatReceiptText(input: string): string {
  const orderId = getOrderId(input);

  // First, replace all \r with <br />
  let formatted = replaceSpacesWithNbsp(input).replace(/\\r/g, '<br />').replace(/\/r/g, '!<br />');

  // Remove the first 3 asterisks and everything in between
  formatted = formatted.replace(/^(.*?\*.*?\*.*?\*)/, '');

  // Then, process lines ending with !
  formatted = formatted
    .split('<br />')
    .map((line) => {
      if (line.endsWith('!')) {
        return `<h1>${line.slice(0, -1)}</h1>`; // Remove the last 1 characters (!) and wrap with <h2>
      }
      return line;
    })
    .join('<br />'); // Rejoin the lines with <br />

  return `<h1>----------------</h1><h1>ORDER:${orderId}</h1><h1>----------------</h1>${formatted}`;
}

// Use a global regex to match all spaces and replace them with "&nbsp;"
function replaceSpacesWithNbsp(text: string) {
  return text.replace(/ /g, '&nbsp;');
}

const acceptOrder = async (
  printerId: string,
  orderId: string,
  printerResponse: AcceptResponse,
): Promise<string> => {
  const password = printerId.slice(-4);
  const response = await fetch(
    `${getPrinterBaseUrl()}/callback?u=${printerId}&p=${password}&ak=Accepted&m=${printerResponse}&o=${orderId}`,
  );
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return await response.text();
};

const getOrderId = (rawText: string) => {
  const updatedRawText = rawText.substring(3); // remove the '*1*' at the start of every order.
  return updatedRawText.substring(0, rawText.lastIndexOf('*')); // left with '3437*...'
};
